/**
 * Payment plans — a client buying a plot, over years.
 *
 * **This is the part that has to be right.** A developer's whole relationship with a buyer is a
 * schedule: a total, a deposit, and instalments that run for two to five years. The single most
 * asked question in the business is "what have I paid and what is left", and an answer that is
 * off by a kobo is an answer somebody argues with.
 *
 * **Why this is a sibling of `billing.ts` and not an extension of it.** `billing.ts` turns a
 * *tenancy* into invoices: a rent cycle, a commencement charge, a deposit held rather than earned,
 * a renewal. Every one of those is a letting idea. A purchase has no cycle — it has a term that
 * ends — no renewal, no landlord to split with, and an end that is a handover. Reaching for the
 * same function would have meant a second set of branches inside every one of its, which is how a
 * module ends up impossible to change. The two do share `packages/core/src/money.ts` and the
 * rounding rule below, which is the part that actually must not diverge.
 *
 * Money is an integer count of minor units throughout, and dates that mean a day are
 * `YYYY-MM-DD` strings — ADR-0003. There is no float anywhere in this file.
 */

import { addMonths, type PlainDate } from "./dates";

export const PURCHASE_FREQUENCIES = ["monthly", "quarterly", "biannual", "annual", "custom"] as const;
export type PurchaseFrequency = (typeof PURCHASE_FREQUENCIES)[number];

/** How many months one instalment covers. `custom` is entered by hand and never generated. */
const MONTHS_PER: Record<Exclude<PurchaseFrequency, "custom">, number> = {
  monthly: 1,
  quarterly: 3,
  biannual: 6,
  annual: 12,
};

export interface PlanTerms {
  /** What was agreed, in minor units. Not the plot's list price, which may have moved. */
  price: number;
  /** Paid up front, before the schedule starts. May be zero. */
  deposit: number;
  frequency: PurchaseFrequency;
  /** How many instalments after the deposit. At least one. */
  instalments: number;
  /** The day the deposit is due. The first instalment falls one period after it. */
  startDate: PlainDate;
}

export interface ScheduledInstalment {
  /** 0 is the deposit; 1 upwards are the instalments. */
  sequence: number;
  label: string;
  dueDate: PlainDate;
  amount: number;
}

export class PlanError extends Error {}

/**
 * Turn terms into a schedule.
 *
 * **The rounding rule: every instalment is the same except the last, which carries the remainder.**
 * A balance of ₦10,000,000 over 3 instalments is 3,333,333.33 each, and a schedule of three equal
 * rounded numbers either loses a kobo or invents one. The alternative — spreading the remainder
 * over the first few — makes two instalments in the middle of a plan differ by one kobo for no
 * reason anybody can explain to a buyer. Putting it all on the last one is the only version that
 * survives being read down a column.
 *
 * **The deposit is instalment zero rather than a separate concept.** It is money owed on a date
 * like any other, it can be part-paid and chased like any other, and a statement that lists it
 * with everything else is a statement a client can add up themselves.
 */
export function planSchedule(terms: PlanTerms): ScheduledInstalment[] {
  if (terms.price <= 0) throw new PlanError("A purchase needs a price.");
  if (terms.deposit < 0) throw new PlanError("A deposit cannot be negative.");
  if (terms.deposit > terms.price) throw new PlanError("The deposit is more than the price.");
  if (terms.instalments < 1) throw new PlanError("A plan needs at least one instalment.");
  if (terms.instalments > 240) throw new PlanError("That is more than twenty years of instalments.");
  if (terms.frequency === "custom") {
    throw new PlanError("A custom plan's instalments are entered by hand, not generated.");
  }

  const rows: ScheduledInstalment[] = [];

  // Instalment zero. Present even when it is nothing, so a schedule always starts on the day the
  // buyer signed and a statement never begins in the middle.
  rows.push({
    sequence: 0,
    label: terms.deposit > 0 ? "Deposit" : "Signed",
    dueDate: terms.startDate,
    amount: terms.deposit,
  });

  const balance = terms.price - terms.deposit;
  if (balance === 0) return rows;

  const step = MONTHS_PER[terms.frequency];
  const each = Math.floor(balance / terms.instalments);
  let allocated = 0;

  for (let n = 1; n <= terms.instalments; n += 1) {
    const last = n === terms.instalments;
    // The last one carries whatever the division left behind, so the parts add back to the whole.
    const amount = last ? balance - allocated : each;
    allocated += amount;
    rows.push({
      sequence: n,
      label: `Instalment ${n} of ${terms.instalments}`,
      dueDate: addMonths(terms.startDate, step * n),
      amount,
    });
  }

  return rows;
}

/** What a schedule adds up to. Always the price, and the test says so for awkward numbers. */
export function scheduleTotal(rows: ScheduledInstalment[]): number {
  return rows.reduce((sum, row) => sum + row.amount, 0);
}

/**
 * A plan typed in by hand — every date and every amount chosen.
 *
 * **The flexible end of the same idea.** `planSchedule` covers the rigid case: a deposit and N
 * equal instalments at a fixed frequency. Real developers also agree things like "₦5m now, ₦10m
 * when the roof goes on, the rest at handover", and a product that can only express equal monthly
 * thirds forces those into a spreadsheet — which is the spreadsheet this replaces.
 *
 * It validates rather than generates, and it is strict about one thing only: **the parts must add
 * up to the price.** A schedule that does not is a schedule the buyer and the developer disagree
 * about, and the disagreement surfaces two years later. Everything else is theirs to choose:
 * uneven amounts, uneven gaps, a zero-amount milestone row to mark a handover date.
 *
 * Dates are sorted rather than rejected for being out of order — somebody adding a forgotten
 * instalment in the middle of a list should not be made to retype the rest.
 */
export function customSchedule(input: {
  price: number;
  deposit: number;
  startDate: PlainDate;
  rows: Array<{ label?: string; dueDate: PlainDate; amount: number }>;
}): ScheduledInstalment[] {
  if (input.price <= 0) throw new PlanError("A purchase needs a price.");
  if (input.deposit < 0) throw new PlanError("A deposit cannot be negative.");
  if (input.deposit > input.price) throw new PlanError("The deposit is more than the price.");
  if (!input.rows.length) throw new PlanError("A plan needs at least one payment after the deposit.");
  if (input.rows.length > 240) throw new PlanError("That is more than two hundred and forty payments.");
  if (input.rows.some((row) => row.amount < 0)) throw new PlanError("A payment cannot be negative.");

  const balance = input.price - input.deposit;
  const entered = input.rows.reduce((sum, row) => sum + row.amount, 0);
  if (entered !== balance) {
    const difference = balance - entered;
    throw new PlanError(
      difference > 0
        ? `The payments are ${difference} short of the balance. They have to add up to what was agreed.`
        : `The payments are ${-difference} more than the balance. They have to add up to what was agreed.`,
    );
  }

  const ordered = [...input.rows].sort((a, b) => (a.dueDate < b.dueDate ? -1 : a.dueDate > b.dueDate ? 1 : 0));

  return [
    {
      sequence: 0,
      label: input.deposit > 0 ? "Deposit" : "Signed",
      dueDate: input.startDate,
      amount: input.deposit,
    },
    ...ordered.map((row, index) => ({
      sequence: index + 1,
      label: row.label?.trim() || `Payment ${index + 1} of ${ordered.length}`,
      dueDate: row.dueDate,
      amount: row.amount,
    })),
  ];
}

export interface InstalmentState {
  sequence: number;
  dueDate: PlainDate;
  amount: number;
  paid: number;
  lateFee: number;
  status: "due" | "part_paid" | "paid" | "waived" | "overdue";
}

export interface PurchaseStanding {
  /** What was agreed. */
  price: number;
  /** Everything owed including late fees, less anything waived. */
  billed: number;
  paid: number;
  /** What is left. Never negative — an overpayment is credit, not a negative balance. */
  outstanding: number;
  /** Owed and past its date. The number a developer's staff are asked for on the phone. */
  arrears: number;
  /** More than was owed. Held rather than subtracted, so nobody reads a negative balance. */
  credit: number;
  /** How far through, in whole percent, for a bar somebody can read across a room. */
  percentPaid: number;
  nextDue: { sequence: number; dueDate: PlainDate; amount: number } | null;
  /** True when every instalment is settled or waived. */
  settled: boolean;
}

/**
 * Where a purchase stands, from its instalments.
 *
 * **Computed, never stored.** A stored balance is a balance that disagrees with the rows the day
 * somebody edits one, and every screen in this module shows this number.
 *
 * A waived instalment is not owed and not paid — it leaves the reckoning entirely rather than
 * counting as settled, or a plan with two waivers reads as further through than it is.
 */
export function purchaseStanding(
  price: number,
  instalments: InstalmentState[],
  today: PlainDate,
): PurchaseStanding {
  let billed = 0;
  let paid = 0;
  let arrears = 0;
  let nextDue: PurchaseStanding["nextDue"] = null;

  const ordered = [...instalments].sort((a, b) => a.sequence - b.sequence);

  for (const row of ordered) {
    if (row.status === "waived") {
      // Not owed and not paid. It leaves the reckoning rather than counting as settled.
      paid += row.paid;
      continue;
    }
    const owed = row.amount + row.lateFee;
    billed += owed;
    paid += row.paid;

    const left = owed - row.paid;
    if (left > 0) {
      if (row.dueDate <= today) arrears += left;
      if (!nextDue) nextDue = { sequence: row.sequence, dueDate: row.dueDate, amount: left };
    }
  }

  const difference = billed - paid;
  const outstanding = Math.max(0, difference);
  const credit = Math.max(0, -difference);

  return {
    price,
    billed,
    paid,
    outstanding,
    arrears,
    credit,
    // Against what was agreed rather than what is billed, so late fees cannot push a buyer past
    // 100% of the price they were quoted.
    percentPaid: price > 0 ? Math.min(100, Math.floor((Math.min(paid, price) / price) * 100)) : 0,
    nextDue,
    settled: outstanding === 0 && ordered.length > 0,
  };
}

/**
 * The state one instalment is in, from its numbers.
 *
 * Derived rather than set by hand, so "overdue" cannot be true on a row that has been paid and
 * "paid" cannot be true on one that is a kobo short. `waived` is the exception: it is a decision
 * somebody made, not a fact about the money, so it is only ever stored.
 */
export function instalmentStatus(
  row: { amount: number; paid: number; lateFee: number; dueDate: PlainDate; waived?: boolean },
  today: PlainDate,
): InstalmentState["status"] {
  if (row.waived) return "waived";
  const owed = row.amount + row.lateFee;
  if (row.paid >= owed) return "paid";
  if (row.dueDate < today) return "overdue";
  if (row.paid > 0) return "part_paid";
  return "due";
}

/**
 * A late fee on one overdue instalment.
 *
 * A percentage of what is still owed rather than of the whole instalment: charging the full
 * penalty to somebody who has paid nine tenths is the sort of thing that ends a plan. Basis points
 * in, minor units out, and it rounds down — a rounding rule that favours us on every one of a
 * hundred instalments is a rounding rule somebody will notice.
 */
export function lateFeeFor(input: { amount: number; paid: number; bps: number }): number {
  if (input.bps <= 0) return 0;
  const owed = Math.max(0, input.amount - input.paid);
  return Math.floor((owed * input.bps) / 10_000);
}

/**
 * What settling the whole balance today would cost, with the early-completion discount applied.
 *
 * **Only on what is not yet due.** A discount for paying early cannot apply to money that was
 * already late, or a buyer four instalments behind is rewarded for it.
 */
export function settlementQuote(input: {
  instalments: InstalmentState[];
  today: PlainDate;
  discountBps: number;
}): { outstanding: number; discount: number; payable: number } {
  let outstanding = 0;
  let futureOwed = 0;

  for (const row of input.instalments) {
    if (row.status === "waived") continue;
    const left = Math.max(0, row.amount + row.lateFee - row.paid);
    outstanding += left;
    if (row.dueDate > input.today) futureOwed += left;
  }

  const discount = input.discountBps > 0 ? Math.floor((futureOwed * input.discountBps) / 10_000) : 0;
  return { outstanding, discount, payable: Math.max(0, outstanding - discount) };
}
